import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';

import { type Board } from '@air/api/types';
import { AlertModal, AlertModalCloseButton, AlertModalTitle } from '@air/primitive-alert-modal';
import { Button } from '@air/primitive-button';

import { useSingleBoardSearchState } from '~/components/BoardSearch/shared/hooks';
import { BoardSelectAllAssetsAndFilesListItem } from '~/components/BoardSelect/components/BoardSelectAllAssetsAndFilesListItem';
import { PrivateBoardSelect, type PrivateBoardSelectProps } from '~/components/BoardSelect/PrivateBoardSelect';
import { UploadDisabledOnPageWarning } from '~/components/Modals/PreUploadModal/UploadDisabledOnPageWarning';
import { BOARDS_SELECT_EMPTY_OPTION } from '~/constants/BoardSearch';
import { ORGANIZE_YOUR_FILES_MODAL, TRANSACTION_MODAL_PRIMARY_CTA } from '~/constants/testIDs';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useIsViewingPeoplePageSelector } from '~/hooks/useIsViewingPeoplePageSelector';
import { useSavedFilterIdSelector } from '~/hooks/useSavedFilterIdSelector';
import { useURLLibraryIdSelector } from '~/hooks/useURLLibraryIdSelector';
import { useURLPersonIdSelector } from '~/hooks/useURLPersonIdSelector';
import { centralizedBoardSelector } from '~/store/centralizedBoard/selectors';
import { type BoardsSelectOption } from '~/types/BoardSearch';
import { canCreateAsset } from '~/utils/permissions/assetPermissions';

const getBoardDisabledMessage = () => "You don't have permission to upload to this board";

export type PreUploadModalProps = {
  onCancel: () => void;
  initialBoard?: PrivateBoardSelectProps['initialBoard'];
  onUpload: (selectedBoard: BoardsSelectOption | null) => void;
  title?: string;
  submitButtonText?: string;
};

export const PreUploadModal = ({
  initialBoard,
  onClose,
  onUpload,
  onCancel,
  title = 'Upload files to…',
  submitButtonText = 'Upload',
}: AirModalProps<PreUploadModalProps>) => {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const isViewingSavedFilter = useSavedFilterIdSelector();
  const isViewingLibraryPage = useURLLibraryIdSelector();
  const isViewingPersonPage = useURLPersonIdSelector();
  const isViewingPeoplePage = useIsViewingPeoplePageSelector();

  const board = useSelector(centralizedBoardSelector);
  const { selectedBoard, isBoardSelected, onSelectBoard, shouldShowSubBoards, isLibrarySelected, onSelectLibrary } =
    useSingleBoardSearchState({
      initialBoard,
      initialLibrary: board?.library,
    });

  const { getBoardPermissions } = useBoardPermissionsCache();

  const isBoardDisabled = useCallback(
    (targetBoard: Board) => !canCreateAsset(getBoardPermissions(targetBoard.id)),
    [getBoardPermissions],
  );

  const handleSelectAllAssets = useCallback(() => {
    onSelectBoard(null);
  }, [onSelectBoard]);

  const handleUpload = useCallback(() => {
    onUpload(selectedBoard);
    onClose();
  }, [onClose, onUpload, selectedBoard]);

  const handleDismissModal = useCallback(() => {
    onSelectBoard(BOARDS_SELECT_EMPTY_OPTION);
    onCancel();
    onClose();
  }, [onCancel, onClose, onSelectBoard]);

  const isUploadDisabledOnPage =
    !!isViewingLibraryPage || !!isViewingSavedFilter || !!isViewingPersonPage || !!isViewingPeoplePage;
  const isSelectedBoardPermissionDisabled = !!selectedBoard && !canCreateAsset(getBoardPermissions(selectedBoard.id));

  return (
    <AlertModal
      className="flex flex-col gap-4"
      data-testid={ORGANIZE_YOUR_FILES_MODAL}
      isOpen
      leastDestructiveRef={cancelButtonRef}
      onDismiss={handleDismissModal}
    >
      <header className="flex items-center justify-between">
        <AlertModalTitle>{title}</AlertModalTitle>
        <AlertModalCloseButton onClick={handleDismissModal} />
      </header>
      <div className="flex flex-col gap-4">
        {!!isViewingSavedFilter && (
          <UploadDisabledOnPageWarning
            title="Unable to upload to a saved search"
            message="Saved searches are unable to receive direct content. Please upload to the All assets & files, or a board"
          />
        )}

        {!!isViewingLibraryPage && (
          <UploadDisabledOnPageWarning
            title="Unable to upload to a Library"
            message="Libraries are unable to receive direct content. Please upload to a board in the library."
          />
        )}

        {!!isViewingPeoplePage && (
          <UploadDisabledOnPageWarning
            title="Unable to upload to People"
            message="People page is unable to receive direct content. Please upload to the All assets & files, or a board."
          />
        )}

        {!!isViewingPersonPage && (
          <UploadDisabledOnPageWarning
            title="Unable to upload to Person"
            message="Person page is unable to receive direct content. Please upload to the All assets & files, or a board."
          />
        )}

        <PrivateBoardSelect
          getBoardDisabledMessage={getBoardDisabledMessage}
          initialBoard={initialBoard}
          isBoardDisabled={isBoardDisabled}
          isBoardSelected={isBoardSelected}
          isLibrarySelected={isLibrarySelected}
          onSelectBoard={onSelectBoard}
          onSelectLibrary={onSelectLibrary}
          shouldShowSubBoards={shouldShowSubBoards}
        >
          <BoardSelectAllAssetsAndFilesListItem isSelected={!selectedBoard} onSelect={handleSelectAllAssets} />
        </PrivateBoardSelect>
      </div>
      <footer className="flex justify-end gap-3">
        <Button appearance="ghost" color="gray" onClick={handleDismissModal} size="large" ref={cancelButtonRef}>
          Cancel
        </Button>
        <Button
          appearance="filled"
          color="blue"
          data-testid={TRANSACTION_MODAL_PRIMARY_CTA}
          disabled={isSelectedBoardPermissionDisabled || (isUploadDisabledOnPage && !selectedBoard)}
          onClick={handleUpload}
          size="large"
        >
          {submitButtonText}
        </Button>
      </footer>
    </AlertModal>
  );
};
